import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout';
import { fmtDateTime } from '@/lib/dateFormat';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import {
  Disc, Eye, EyeOff, Save, Trash2, Loader2, CheckCircle2,
  AlertCircle, TestTube, ShieldCheck, ArrowRight, Info,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface CredentialsStatus {
  configured: boolean;
  username?: string;
  companyId?: string | null;
  salesOrg?: string;
  updatedAt?: string;
}

const inputCls =
  'w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 placeholder:text-slate-600';

const SALES_ORG_OPTIONS = [
  { value: '1500', label: '1500 — USD only' },
  { value: '1000', label: '1000 — CAD + USD' },
  { value: '4000', label: '4000 — CAD only' },
  { value: '5000', label: '5000 — AUD' },
  { value: '6000', label: '6000 — GBP' },
  { value: '7000', label: '7000 — EUR' },
];

export function WheelProsSettings() {
  const { token } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // Current saved status
  const [status, setStatus] = useState<CredentialsStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [salesOrg, setSalesOrg] = useState('1500');
  const [showPassword, setShowPassword] = useState(false);

  // Action states
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; authMode?: string; error?: string } | null>(null);

  async function loadStatus() {
    setLoadingStatus(true);
    try {
      const r = await fetch(`${BASE}/api/wheelpros/credentials`, { headers: authHeaders });
      if (!r.ok) throw new Error(r.statusText);
      const data = await r.json();
      setStatus(data);
      if (data.companyId) setCompanyId(data.companyId);
      if (data.salesOrg) setSalesOrg(data.salesOrg);
    } catch {
      setStatus({ configured: false });
    } finally {
      setLoadingStatus(false);
    }
  }

  useEffect(() => { loadStatus(); }, []);

  async function handleTest() {
    if (!username.trim() || !password.trim()) {
      toast({ title: 'Enter credentials first', description: 'Username and password are required to test.', variant: 'destructive' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await fetch(`${BASE}/api/wheelpros/credentials/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ username: username.trim(), password: password.trim() }),
      });
      const data = await r.json();
      setTestResult(data);
    } catch (e: any) {
      setTestResult({ ok: false, error: e.message ?? 'Connection failed' });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    if (!username.trim() || !password.trim()) {
      toast({ title: 'Missing fields', description: 'Username and password are required.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/api/wheelpros/credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          username: username.trim(),
          password: password.trim(),
          companyId: companyId.trim() || undefined,
          salesOrg,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).error ?? r.statusText);
      toast({ title: 'Credentials saved', description: 'Your WheelPros account is connected.' });
      queryClient.invalidateQueries({ queryKey: ['wheelpros-status'] });
      queryClient.invalidateQueries({ queryKey: ['wp-facets'] });
      setUsername('');
      setPassword('');
      setTestResult(null);
      await loadStatus();
    } catch (e: any) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Remove your WheelPros credentials? You will lose access to the catalog until you reconnect.')) return;
    setDeleting(true);
    try {
      await fetch(`${BASE}/api/wheelpros/credentials`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      toast({ title: 'Credentials removed' });
      queryClient.invalidateQueries({ queryKey: ['wheelpros-status'] });
      queryClient.invalidateQueries({ queryKey: ['wp-facets'] });
      setStatus({ configured: false });
      setCompanyId('');
      setSalesOrg('1500');
    } catch (e: any) {
      toast({ title: 'Failed to remove', description: e.message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Disc className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold text-gradient">WheelPros Connection</h1>
              <p className="text-sm text-muted-foreground">Connect your personal WheelPros account to access the catalog.</p>
            </div>
          </div>
        </div>

        {/* Current status */}
        {loadingStatus ? (
          <div className="glass-panel rounded-xl p-5 flex items-center gap-3 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking connection status…
          </div>
        ) : status?.configured ? (
          <div className="glass-panel rounded-xl p-5 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium text-emerald-400">Connected</span>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <div className="text-muted-foreground">Username</div>
              <div className="font-mono text-foreground">{status.username}</div>
              {status.companyId && <>
                <div className="text-muted-foreground">Company ID</div>
                <div className="font-mono text-foreground">{status.companyId}</div>
              </>}
              <div className="text-muted-foreground">Sales Org</div>
              <div className="font-mono text-foreground">{status.salesOrg}</div>
              {status.updatedAt && <>
                <div className="text-muted-foreground">Last updated</div>
                <div className="text-muted-foreground">{fmtDateTime(status.updatedAt)}</div>
              </>}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => setLocation('/wheel-pros/sync')}
                className="flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                Open Stock Sync <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-sm text-amber-400">
            <AlertCircle className="w-4 h-4 shrink-0" />
            No WheelPros account connected. Enter your credentials below.
          </div>
        )}

        {/* Credentials form */}
        <div className="glass-panel rounded-xl p-6 space-y-5">
          <h2 className="text-sm font-semibold text-foreground">
            {status?.configured ? 'Update Credentials' : 'Connect Account'}
          </h2>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Username (email)</label>
              <input
                type="text"
                autoComplete="off"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder={status?.configured ? status.username : 'you@example.com'}
                className={inputCls}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={status?.configured ? '••••••••' : 'Your WheelPros password'}
                  className={`${inputCls} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Company ID <span className="text-slate-600">(optional — for pricing)</span>
              </label>
              <input
                type="text"
                value={companyId}
                onChange={e => setCompanyId(e.target.value)}
                placeholder="e.g. 123456"
                className={`${inputCls} font-mono`}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Sales Organisation</label>
              <select
                value={salesOrg}
                onChange={e => setSalesOrg(e.target.value)}
                className={inputCls}
              >
                {SALES_ORG_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Encryption notice */}
          <div className="flex items-start gap-2 text-[11px] text-slate-600">
            <ShieldCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
            Credentials are encrypted at rest using AES-256 before being stored.
          </div>

          {/* Test result */}
          {testResult && (
            <div className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border text-sm ${
              testResult.ok
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                : 'bg-red-500/10 border-red-500/20 text-red-400'
            }`}>
              {testResult.ok
                ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
              <span>
                {testResult.ok
                  ? `Connection successful (${testResult.authMode === 'pdp' ? 'AWS SigV4' : 'Bearer token'} auth)`
                  : testResult.error ?? 'Connection failed'}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleTest}
              disabled={testing || !username.trim() || !password.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border text-sm text-foreground hover:text-foreground transition-all disabled:opacity-40"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
              Test Connection
            </button>

            <button
              onClick={handleSave}
              disabled={saving || !username.trim() || !password.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-sm text-primary hover:text-foreground transition-all disabled:opacity-40"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Credentials
            </button>
          </div>
        </div>

        {/* Danger zone */}
        {status?.configured && (
          <div className="glass-panel rounded-xl p-5 border border-red-500/10 space-y-3">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Danger Zone</h2>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-foreground">Remove WheelPros credentials</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  You will lose access to the catalog until you reconnect.
                </p>
              </div>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-sm text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-all whitespace-nowrap disabled:opacity-40"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Disconnect
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
